import type { Rubric } from '../core/rubric';
import type { ScoreResponse } from '../core/scoring';
import { posthogHeaders } from './analytics/client';

export type { ScoreResponse, ItemResult } from '../core/scoring';

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string; reason?: string; limit?: number; upstreamStatus?: number };

/**
 * Every call carries the Clerk session token. `getToken` is passed in rather than imported
 * so components stay testable without mounting a ClerkProvider.
 */
export type GetToken = () => Promise<string | null>;

export async function call<T>(getToken: GetToken, path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  let token: string | null = null;
  try {
    token = await getToken();
  } catch {
    return { ok: false, status: 0, error: 'no_session' };
  }

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        ...posthogHeaders(),
        ...(init.headers ?? {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    return { ok: false, status: 0, error: 'network' };
  }

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      error: typeof body.error === 'string' ? body.error : 'unknown',
      ...(typeof body.upstreamStatus === 'number' ? { upstreamStatus: body.upstreamStatus } : {}),
      ...(typeof body.reason === 'string' ? { reason: body.reason } : {}),
      ...(typeof body.limit === 'number' ? { limit: body.limit } : {}),
    };
  }

  return { ok: true, data: body as T };
}

export const score = (getToken: GetToken, body: { text: string; rubric: Rubric }) =>
  call<ScoreResponse>(getToken, '/api/score', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

export const structureRubric = (getToken: GetToken, text: string) =>
  call<{ rubric: Rubric; warnings: string[] }>(getToken, '/api/rubric/structure', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ text }),
  });
